import type { MetaDescriptor } from 'react-router'
// Imported (not a `public/` path) so Vite fingerprints it into
// `/assets/og-image-<hash>.png` — see the header of scripts/build-og-image.ts
// for why that matters at the CloudFront layer, and for how to regenerate it.
import ogImageUrl from './images/og-image.png'
import { absoluteUrl, SITE_NAME } from './site'

// Every page title ends with this, so `og:title` can drop it: the card already
// carries the site name in `og:site_name`, and "Terms | Vital Care Finder" under
// a heading that reads "Vital Care Finder" says it twice.
const TITLE_SUFFIX = ` | ${SITE_NAME}`

// Describes the card, not the page — it is the same image on every route
// (per-provider cards are out of scope), so a page-specific alt would be a lie.
const OG_IMAGE_ALT = `${SITE_NAME}: find the right doctor for you`

// Pinned in the tags as well as baked into the file, because several consumers
// (X, LinkedIn) lay out the card before they have fetched the image.
const OG_IMAGE_WIDTH = '1200'
const OG_IMAGE_HEIGHT = '630'

type PageMetaOptions = {
  /** The full document title, including the `| Vital Care Finder` suffix. */
  title: string
  /** The meta description, reused verbatim for `og:description`. */
  description: string
  /**
   * The path this page wants indexed — NOT necessarily the path being served.
   *
   * Omitted only when there is no such page: results.tsx and provider.tsx call
   * this from an error boundary render too (`loaderData` is undefined when the
   * loader threw), and a 404 must not claim itself as the canonical version of
   * anything. `og:url` drops out with it, for the same reason — nobody shares a
   * page that failed to load.
   *
   * Explicit rather than derived from the request, because two routes
   * deliberately point elsewhere: results.tsx sends duplicate specialty slugs to
   * their primary (`family-doctor` → `primary-care-provider`, which share a NUCC
   * code and therefore a roster), and provider.tsx sends every decorative slug
   * variant to the canonical spelling (VIT-17797). For every other route this is
   * simply the route's own path.
   *
   * Path-only, with no query string, which is the point on results.tsx: it
   * accepts arbitrary `?insurance=<uuid>` values, so each param combination is
   * otherwise a distinct indexable URL competing with the clean one.
   */
  canonicalPath?: string | undefined
}

/**
 * The shared `<head>` tag set for an indexable page: title, description,
 * self-referencing canonical, Open Graph and Twitter card (VIT-17746).
 *
 * The canonical is absolute (VIT-18029). A relative one is valid — it resolves
 * against the document URL — but Google's own canonicalization guidance
 * recommends absolute, and three things here want it:
 *
 *  - Scraped and mirrored content, which is the real argument for this site. A
 *    provider directory with curated ratings and cost data gets republished. An
 *    absolute canonical in the copied markup credits vit.al; a relative one
 *    resolves against the scraper's domain and credits them.
 *  - Migration safety: a future protocol, domain or extra hostname would have
 *    relative canonicals resolving to whichever host the crawler happened to
 *    fetch, which defeats the tag.
 *  - No reliance on base-URL resolution — a `<base>` tag or an unexpected
 *    serving host would otherwise change every canonical on the site at once.
 *
 * `og:url` and `og:image` were already absolute, Open Graph requiring it. So
 * were the JSON-LD nodes, sitemap.ts and llms.ts. A preview stage therefore
 * emits vit.al canonicals — accepted, and the same trade-off those already make.
 * What makes it safe is that no preview is indexed: VIT-17997 stamps
 * `noindex, nofollow` on off-production stages in three independent layers (see
 * app/robots.ts), and a page a crawler has been told not to index is not one it
 * consolidates duplicates onto, so its canonical never gets acted on.
 *
 * Deliberately NOT applied to `/mcp-demo`, `/__geo` or `/__insurance`: all three
 * are `robots: noindex`, and a canonical on a page asking not to be indexed is
 * contradictory signalling.
 */
export const pageMeta = ({ title, description, canonicalPath }: PageMetaOptions): MetaDescriptor[] => {
  const socialTitle = title.endsWith(TITLE_SUFFIX) ? title.slice(0, -TITLE_SUFFIX.length) : title
  const imageUrl = absoluteUrl(ogImageUrl)

  return [
    { title },
    { name: 'description', content: description },
    ...(canonicalPath != null
      ? [{ tagName: 'link', rel: 'canonical', href: absoluteUrl(canonicalPath) } as const]
      : []),

    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:title', content: socialTitle },
    { property: 'og:description', content: description },
    ...(canonicalPath != null ? [{ property: 'og:url', content: absoluteUrl(canonicalPath) }] : []),
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:width', content: OG_IMAGE_WIDTH },
    { property: 'og:image:height', content: OG_IMAGE_HEIGHT },
    { property: 'og:image:alt', content: OG_IMAGE_ALT },

    // `name`, not `property`: the Twitter tags predate RDFa and are specified on
    // `name`. Only the four that carry information the og:* tags above don't
    // already cover for X's parser.
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: socialTitle },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: OG_IMAGE_ALT },
  ]
}
